import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { motion } from 'framer-motion';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
};

const fadeIn = {
  initial: { opacity: 0 },
  whileInView: { opacity: 1 },
  viewport: { once: true },
  transition: { duration: 1, ease: "easeOut" }
};

const counterVariants = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

export const Home = () => {
  return (
    <div className="min-h-screen bg-white overflow-hidden">
      <Navbar />
      
      {/* Hero Section */}
      <section 
        className="relative h-screen flex items-center overflow-hidden"
        data-testid="home-hero"
      >
        <motion.div 
          className="absolute inset-0 bg-cover bg-center grayscale"
          style={{ 
            backgroundImage: `url(https://customer-assets.emergentagent.com/job_catworth-group/artifacts/p04go0v9_WhatsApp%20Image%202026-03-12%20at%2011.21.18.jpeg)` 
          }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </motion.div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 w-full">
          <motion.p 
            className="text-xs font-bold tracking-[0.2em] uppercase text-white/70 mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            South African Steel Manufacturing
          </motion.p>
          <motion.h1 
            className="text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter uppercase text-white leading-[0.9] mb-6"
            style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            CATWORTH<br />GROUP
          </motion.h1>
          <motion.p 
            className="text-2xl md:text-3xl font-light text-white/90 mb-8"
            style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
          >
            Wear Solutions Built<br />
            for Heavy Industry
          </motion.p>
          <motion.p 
            className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
          >
            A diversified industrial group serving the mining and heavy industry sectors. Through our specialized divisions, we deliver durable wear solutions and precision-engineered components designed for demanding operating conditions.
          </motion.p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 md:py-32" data-testid="home-about">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            <motion.div {...fadeInUp}>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-neutral-500 mb-4">
                About Us
              </p>
              <h2 
                className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6"
                style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
              >
                About Catworth Group
              </h2>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                Catworth Group is a South African industrial manufacturing company focused on delivering durable wear solutions and precision-engineered components to the mining and heavy industry sectors.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed mb-6">
                Built on years of hands-on experience, our specialized divisions provide practical, performance-driven solutions across mill lining systems, wear parts, laser cutting, and precision turning. We understand the realities of heavy industry and manufacture with reliability, consistency, and longevity in mind.
              </p>
              <p className="text-lg text-neutral-600 leading-relaxed">
                Our strength lies in our ability to adapt, solve problems on the ground, and deliver quality components that perform under demanding operating conditions. With a committed team and a deep understanding of materials and processes, Catworth Group continues to support clients with solutions that work where it matters most – in the field.
              </p>
            </motion.div>
            <motion.div 
              className="relative aspect-[4/3] overflow-hidden"
              {...fadeIn}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_catworth-group/artifacts/q7hw2xh1_image.png" 
                alt="Catworth Group manufacturing facility"
                className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 border-t border-neutral-200" data-testid="home-stats">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center">
            {[
              { value: '20+', label: 'Years Experience' },
              { value: 'Wear Solutions', label: 'Specialists' },
              { value: '5', label: 'Specialized Divisions' },
              { value: 'Trusted', label: 'By Mining Operations' },
            ].map((stat, index) => (
              <motion.div 
                key={stat.label}
                {...counterVariants}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <p 
                  className="text-4xl md:text-5xl font-bold tracking-tight mb-2"
                  style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
                >
                  {stat.value}
                </p>
                <p className="text-xs text-neutral-500 uppercase tracking-widest">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
